import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Card as CardMaterial,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  CardMedia,
  Tooltip,
  createMuiTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteBlog,
  getBlogById,
  postBlog,
  updateBlog,
} from "../../../../api/blog";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import Table, {
  buttonsStyle,
  dateFormatter,
} from "../../../components/tables/table";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import { SERVER_URL } from "../../../../api";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import { Delete, Visibility } from "@material-ui/icons";
import Editor from "../../../components/editor/Editor";
import { getCategorias } from "../../../../api/categoria";
import { getTags } from "../../../../api/tag";
import { Autocomplete } from "@material-ui/lab";
import MultiTabBlock from "../../../components/MultiTabBlock";
import { generateFriendlyUrl } from "../../../../utils/helpers";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyBlog() {
  return {
    content: {},
    title: {},
    imageUrl: "",
    tituloEnlace: {},
    enlace: {},
    descripcionEnlace: {},
    active: true,
  };
}

// Helper function to get image URL (supports both base64 and file paths)
const getImageUrl = (imageUrl) => {
  if (!imageUrl || imageUrl === "") return "";
  // If it's base64 (starts with data:image), use it directly
  if (imageUrl.startsWith("data:image")) {
    return imageUrl;
  }
  // Otherwise, it's a file path, construct the full URL
  return `${SERVER_URL}${imageUrl.startsWith("/") ? imageUrl : `/${imageUrl}`}`;
};

export default function EditBlogPage() {
  const [blog, setBlog] = useState(getEmptyBlog());
  const imageInputRef = useRef(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const blogId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    const fetch = async () => {
      try {
        const resCategoria = await getCategorias();
        const resTag = await getTags();

        setCategorias(resCategoria.data);
        setTags(resTag.data);
        if (!blogId) {
          disableLoadingData();
          return;
        }
        getBlogById(blogId)
          .then((res) => {
            if (res.status === 200) {
              const blog = res.data;
              setBlog(blog);
              const cateIds = blog.categorias;
              setSelectedCategorias(
                resCategoria.data.filter((x) => cateIds.includes(x._id))
              );

              const tagIds = blog.tags;
              setSelectedTags(
                resTag.data.filter((x) => tagIds.includes(x._id))
              );

              disableLoadingData();
            }
          })
          .catch((error) => {
            alertError({ error: error, customMessage: "Could not get blog." });
            history.push("/blogs");
          });
      } catch (error) {
        console.log(error);
      }
    };
    fetch();
  }, [blogId, disableLoadingData, history]);

  function saveBlog() {
    let saveBlog = blog;
    const cateIds = selectedCategorias.map((x) => x._id);
    const tagIds = selectedTags.map((x) => x._id);
    saveBlog.categorias = cateIds;
    saveBlog.tags = tagIds;
    if (!blogId) {
      postBlog(saveBlog, selectedImage)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Blog successfully created.",
            });
            history.push("/blogs");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save blog.",
          });
        });
    } else {
      updateBlog(blogId, saveBlog, selectedImage)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/blogs");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element, lang) => (event) => {
    if (lang) {
      if (event.target.value === " ") return;

      if (!blog[element]) blog[element] = {};
      let newText = blog[element];
      newText[lang] = event.target.value;

      // Si se está editando el título, actualizar automáticamente el enlace
      if (element === "title") {
        if (!blog.enlace) blog.enlace = {};
        blog.enlace[lang] = generateFriendlyUrl(event.target.value);
      }

      setBlog({ ...blog, [element]: newText });
    } else setBlog({ ...blog, [element]: event.target.value });
  };

  const handleChangeEditor = (element, value, lang) => {
    if (value === " ") return;
    if (lang) {
      if (!blog[element]) blog[element] = {};
      let newText = blog[element];
      newText[lang] = value;
      setBlog({ ...blog, [element]: newText });
    } else setBlog({ ...blog, [element]: value });
  };

  const renderMultilanguageTabContent = (lang) => {
    return (
      <>
        <div className="d-flex align-text-center">
          <div className="col-md-6">
            <div className="r-flex align-text-center">
              <div className="col-md-12">
                <TextField
                  id={`fecha_publi`}
                  label="Fecha de publicación"
                  value={blog.fecha_publi ? blog.fecha_publi.slice(0, 10) : ""}
                  onChange={handleChange("fecha_publi")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  type="date"
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-12">
                <TextField
                  id={`title`}
                  label="Titulo"
                  value={blog.title && blog.title[lang] ? blog.title[lang] : ""}
                  onChange={handleChange("title", lang)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-md-12">
                <Editor
                  body={(blog.content && blog.content[lang]) || ""}
                  setBody={(new_body) =>
                    handleChangeEditor("content", new_body, lang)
                  }
                  className="max-height"
                />
              </div>
              <div className="col-md-12">
                <TextField
                  id={`link`}
                  label="Link"
                  value={blog.link && blog.link[lang] ? blog.link[lang] : ""}
                  onChange={handleChange("link", lang)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
              <div className="col-12 col-md-12 text-center my-2">
                <Autocomplete
                  className="w-100 mr-5"
                  id="autocomplete-categorias"
                  filterSelectedOptions
                  options={categorias || []}
                  multiple
                  getOptionLabel={(option) => option?.name?.[lang] || option?.name || ""}
                  value={selectedCategorias || []}
                  onChange={(event, selected) => {
                    setSelectedCategorias(selected);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Seleccionar Categorias"
                    />
                  )}
                />
              </div>
              <div className="col-12 col-md-12 text-center my-2">
                <Autocomplete
                  className="w-100 mr-5"
                  id="autocomplete-tags"
                  filterSelectedOptions
                  options={tags || []}
                  multiple
                  getOptionLabel={(option) => option?.name?.[lang] || option?.name || ""}
                  value={selectedTags || []}
                  onChange={(event, selected) => {
                    setSelectedTags(selected);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Seleccionar Tags"
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <div className="col-md-6 mt-2">
            <CardMaterial>
              {(selectedImage || (blog.imageUrl && blog.imageUrl !== "")) && (
                <CardMedia
                  component="img"
                  alt="Blog image"
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "auto",
                    maxHeight: "600px",
                  }}
                  image={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : getImageUrl(blog.imageUrl)
                  }
                />
              )}
            </CardMaterial>
            <label htmlFor={"upload-media"} className="ml-0 my-4">
              <input
                ref={imageInputRef}
                style={{ display: "none" }}
                id={"upload-media"}
                name={"upload-media"}
                type="file"
                accept={"image/*"}
                onChange={(e) => {
                  setSelectedImage(e.target.files[0]);
                }}
              />
              <Button
                style={{ marginRight: "15px" }}
                color="secondary"
                component="span"
                variant="outlined"
              >
                {selectedImage || (blog.imageUrl && blog.imageUrl !== "")
                  ? "cambiar imagen"
                  : "subir imagen"}
              </Button>
            </label>
            {(selectedImage || (blog.imageUrl && blog.imageUrl !== "")) && (
              <>
                <Tooltip title={"Preview"}>
                  <Button
                    size="small"
                    onClick={() => setOpenPreviewDialog(1)}
                    style={{
                      ...buttonsStyle,
                      marginRight: "15px",
                    }}
                  >
                    <Visibility />
                  </Button>
                </Tooltip>
                <PreviewDialog
                  title={"Preview"}
                  open={openPreviewDialog === 1}
                  setOpen={setOpenPreviewDialog}
                  file={selectedImage}
                  src={
                    selectedImage
                      ? URL.createObjectURL(selectedImage)
                      : getImageUrl(blog.imageUrl)
                  }
                />
                <Tooltip title={"Delete"}>
                  <Button
                    size="small"
                    onClick={() => {
                      setSelectedImage(null);
                      setBlog({
                        ...blog,
                        imageUrl: null,
                      });
                      if (imageInputRef.current) {
                        imageInputRef.current.value = "";
                      }
                    }}
                  >
                    <Delete />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderContentDatosGenerales = () => {
    return (
      <>
        <MultilanguageTabBlock
          multilanguageTabContent={renderMultilanguageTabContent}
        />
      </>
    );
  };

  const renderMultilanguageTabContentEnlace = (lang) => {
    return (
      <>
        <TextField
          id={`enlace`}
          label="Url amigable"
          value={blog.enlace && blog.enlace[lang] ? blog.enlace[lang] : ""}
          onChange={handleChange("enlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`tituloEnlace`}
          label="Titulo SEO"
          value={
            blog.tituloEnlace && blog.tituloEnlace[lang]
              ? blog.tituloEnlace[lang]
              : ""
          }
          onChange={handleChange("tituloEnlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
        <TextField
          id={`descripcionEnlace`}
          label="Descripción SEO"
          value={
            blog.descripcionEnlace && blog.descripcionEnlace[lang]
              ? blog.descripcionEnlace[lang]
              : ""
          }
          onChange={handleChange("descripcionEnlace", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
      </>
    );
  };

  const renderContentEnlace = () => {
    return (
      <>
        <MultilanguageTabBlock
          multilanguageTabContent={renderMultilanguageTabContentEnlace}
        />
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          <CardHeader title="Editar blog"></CardHeader>
          <CardBody>
            <MultiTabBlock
              tabs={[
                {
                  enable: true,
                  title: "Datos Generales",
                  renderContent: renderContentDatosGenerales,
                },
                {
                  enable: blogId !== undefined,
                  title: "SEO",
                  renderContent: renderContentEnlace,
                },
              ]}
            />
          </CardBody>
        </Card>
        <Button
          onClick={() => history.push("/blogs")}
          variant="outlined"
          style={{ marginRight: "20px" }}
        >
          Back
        </Button>
        <Button
          onClick={() => saveBlog()}
          variant="outlined"
          color="primary"
          style={{ marginRight: "20px" }}
        >
          Save blog
        </Button>
        {blogId && (
          <>
            <MuiThemeProvider theme={theme}>
              <Button
                onClick={() => setOpenConfirmDialog(true)}
                variant="outlined"
                color="secondary"
              >
                Delete blog
              </Button>
            </MuiThemeProvider>
            <ConfirmDialog
              title={"Are you sure you want to delete this blog?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteBlog(blogId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Blog deleted successfully",
                      });
                      history.push("/blogs");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete blog.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
